// Curated qualitative palette for company/group colours. Picked greedily to
// maximise the minimum pairwise perceptual distance (CIE DeltaE ~15), so no two
// entries look alike. Using a fixed palette instead of hashing a hue avoids the
// clustering we got from `color-hash` (many company names hashed into the
// green band and looked identical).
const COMPANY_PALETTE = [
  "#c32222",
  "#0df20d",
  "#34b4f4",
  "#0909a5",
  "#f2a60d",
  "#f434f4",
  "#307e5f",
  "#0df2e3",
  "#7e6430",
  "#0966a5",
  "#a50971",
  "#7e9816",
  "#f2f20d",
  "#c48464",
  "#f43474",
  "#7654d4",
  "#1bbb7b",
  "#168698",
  "#7e303a",
  "#a58609",
  "#648ac4",
  "#a55109",
  "#2baba3",
  "#f24a0d",
  "#c4b764",
  "#64307e",
  "#238b23",
  "#44cfe4",
  "#30447e",
  "#91c464",
  "#c464a4",
  "#34f4a7",
];

// Deterministic FNV-1a hash so a given name always maps to the same palette
// entry, regardless of card ordering, of how many names there are, or of which
// screen is drawing it. We index from the high bits (`/ 2**32 * length`) rather
// than `% length`: FNV's low bits cluster, but its high bits are well mixed.
const paletteIndex = (name, length) => {
  let hash = 0x811c9dc5;
  const key = String(name);
  for (let i = 0; i < key.length; i++)
    hash = Math.imul(hash ^ key.charCodeAt(i), 0x01000193);
  return Math.floor(((hash >>> 0) / 2 ** 32) * length);
};

const paletteColor = (palette) => (name) =>
  palette[paletteIndex(name, palette.length)];

const companyPaletteColor = paletteColor(COMPANY_PALETTE);

// Curated qualitative palette, guaranteed distinct, shared by the focused
// varieties of the PCA and the EvolutionChart colorway.
const EXTENDED_PALETTE = [
  "#845EC2",
  "#D65DB1",
  "#FF6F91",
  "#FF9671",
  "#FFC75F",
  "#F9F871",
  "#9BDE7E",
  "#4BBC8E",
  "#039590",
];

export const THEME = Object.freeze({
  background: "none",
  indicators: "darkgray",
  baseColors: [
    "#8FBF48",
    "#0c2800",
    "#6f9e2a",
    "#134400",
    "#4f7f00",
    "#2f6100",
  ],
  extendedPalette: EXTENDED_PALETTE,
  // Modality colours. Mirrors the ColorHash `.hex(name)` interface its six call
  // sites already use, but resolves to a curated palette entry instead of a free
  // hue at fixed saturation and lightness: two modalities whose hash landed on
  // neighbouring hues came out identical, which is R3-01.
  // What this does NOT fix: each modality still draws its palette entry from its
  // own name, blind to the others, so two of them collide once in two from four
  // modalities on, and nine times in ten from six on. That is the birthday
  // problem, not a weakness of this hash, and no hash function does better with
  // nine entries. Distinctness needs the value's rank in the list of values its
  // attribute holds, which is what CLOV-728 lands (`hooks/useAttributeColor`).
  modalityColorHash: { hex: paletteColor(EXTENDED_PALETTE) },
  // Company/group colours (campaign tiles, site/campaign headers). Mirrors the
  // ColorHash `.hex(name)` interface but resolves to a curated, guaranteed-
  // distinct palette entry instead of a hashed hue.
  companyColorHash: { hex: companyPaletteColor },
  // Tableau 10, ordered for sequential distinctness: the first N entries are
  // maximally separated, so 2-3 modalities land on clearly different colors.
  // Overflow past this list is interpolated across it (see useAttributeColor).
  categoricalPalette: [
    "#4E79A7",
    "#F28E2B",
    "#E15759",
    "#76B7B2",
    "#59A14F",
    "#EDC948",
    "#B07AA1",
    "#FF9DA7",
    "#9C755F",
    "#BAB0AC",
  ],
});

export const MAP_TILE_LAYERS = Object.freeze({
  SATELLITE: {
    name: "sat",
    url: "https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}",
  },
  OPEN_STREET_MAP: {
    name: "osm",
    url: "https://tile.openstreetmap.org/{z}/{x}/{y}.png",
  },
  TOPO_MAP: {
    name: "topo",
    url: "https://server.arcgisonline.com/ArcGIS/rest/services/World_Topo_Map/MapServer/tile/{z}/{y}/{x}.png",
  },
  DARK: {
    name: "dark",
    url: "https://basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png",
  },
});

export const MAP_MODES = Object.freeze({
  MARKERS: "markers",
  CLUSTER: "cluster",
  TRIAL: "trial",
});

export const MAP_DISPLAY_MODES = Object.freeze({
  FIELD_MAP: "fieldmap",
  PLOT_MAP: "plotmap",
  RESULTS: "results",
  FIELD_BOOK: "fieldbook",
});

// The Results tab holds both colourings: the trait (RESULTS) or an attribute
// (FIELD_BOOK).
export const isResultsTab = (mode) =>
  mode === MAP_DISPLAY_MODES.RESULTS || mode === MAP_DISPLAY_MODES.FIELD_BOOK;

// Fill of a plot with no value, whether coloured by trait or by attribute.
export const NA_COLOR = "#A9A9A9";

export const DEFAULT_MAP_LAYERS = Object.freeze({
  FEATURES_LAYER: "features_layer",
  ORTHO_LAYER: "ortho_layer",
});

export const MAP_LAYER_TYPES = Object.freeze({
  GEOJSON: {
    name: "GEOJSON",
    icon: "fa-solid fa-table-cells",
    opacityModifiable: true,
  },
  ORTHO: {
    name: "ORTHO",
    icon: "fa-solid fa-layer-group",
    opacityModifiable: true,
  },
  SCOUTING: {
    name: "SCOUTING",
    icon: "fa-solid fa-camera",
    opacityModifiable: false,
  },
});

export const APPLICATION_ENABLED = "APPLICATION_ENABLED";
export const APPLICATION_FREEZED = "APPLICATION_FREEZED";

export const METADATA_TYPE = "METADATA_TYPE";
export const DATASET_TYPE = "DATASET_TYPE";
export const CONFIG_TYPE = "CONFIG_TYPE";
export const CAMERA_PROFILE_TYPE = "CAMERA_PROFILE_TYPE";
export const SVM_TRAININGSET_TYPE = "SVM_TRAININGSET_TYPE";
export const VECTOR_CONFIG_TYPE = "VECTOR_CONFIG_TYPE";
export const CAMERA_SETUP_TYPE = "CAMERA_SETUP_TYPE";
export const GRP_PARAMETERS_TYPE = "GRP_PARAMETERS_TYPE";
export const NNT_PARAMETERS_TYPE = "NNT_PARAMETERS_TYPE";
export const LUT_PARAMETERS_TYPE = "LUT_PARAMETERS_TYPE";

export const PHENOSCRIPT_LABEL_MARKERS_TYPE = "PHENOSCRIPT_LABEL_MARKERS_TYPE";
export const PHENOSCRIPT_PLOTS_TYPE = "PHENOSCRIPT_PLOTS_TYPE";
export const PHENOSCRIPT_REFLECTANCE_TYPE = "PHENOSCRIPT_REFLECTANCE_TYPE";
export const PHENOSCRIPT_TRAINING_TYPE = "PHENOSCRIPT_TRAINING_TYPE";
export const PHENOSCRIPT_VIGNETTES_TYPE = "PHENOSCRIPT_VIGNETTES_TYPE";
export const PHOTOSCAN_PROJECT_TYPE = "PHOTOSCAN_PROJECT_TYPE";

export const ADDITIONAL_DATA_TYPE = "ADDITIONAL_DATA_TYPE";

export const UPLOAD_EMPTY = "UPLOAD_EMPTY";
export const UPLOAD_READY = "UPLOAD_READY";
export const UPLOAD_IN_PROGRESS = "UPLOAD_IN_PROGRESS";
export const UPLOAD_DONE = "UPLOAD_DONE";
export const UPLOAD_MISSING_DATA = "UPLOAD_MISSING_DATA";

export const USER_ROLE_ADMIN = "Administrator";
export const USER_ROLE_BACKOFFICE = "Back office";
export const USER_ROLE_REGULAR = "Regular user";

export const OPTIONS_SYSTEM = [
  { value: "AV", label: "Aircraft System" },
  { value: "satellite", label: "Satellite" },
  { value: "phenomobile", label: "Phenomobile" },
  { value: "literal", label: "Literal" },
];

export const default_trait = {
  name: "",
  definition: "",
  unit: "",
  crops: "",
  sensor: "",
  ref_paper: "",
  style: {
    opacity: true,
    color: ["#ff0000", "#00ff20"],
    step: 5,
    intervalDefault: false,
    intervalMin: 0,
    intervalMax: 100,
    display_on_map: true,
    display_on_analysis: true,
  },
  technical_name: "",
  image: null,
};

export const SESSION_UPLOAD_TOOLTIP_SUCCESS =
  "Your data is successfully uploaded.";
export const SESSION_UPLOAD_TOOLTIP_PARTIAL =
  "You defined this session as partial. Thank you to provide missing files then mark it as complete.";
export const SESSION_UPLOAD_TOOLTIP_INCOMPLETE =
  "Your session seems to be incomplete. Please review your uploaded data.";
export const SESSION_UPLOAD_STATUS_CLIENT = [
  {
    value: "partial",
    title: "Partial",
    label: "I still have more data to add - I'll come back to finish later.",
  },
  {
    value: "complete",
    title: "Complete",
    label:
      "Everything has been uploaded - this session is ready for processing.",
  },
];

export const FILE_MAX_SIZE = 50000000;
export const FILE_MAX_SIZE_MB = FILE_MAX_SIZE / 1000000;

const GEO_STD = [".geojson", ".kml", ".kmz", ".gpkg"];
export const GEO_EXT = {
  plot_file: GEO_STD,
  gcp_file: GEO_STD,
};
export const GEO_EXT_TOOLTIP = `Accepted formats are geojson, KMZ, KML and geopackage. (MAX ${FILE_MAX_SIZE_MB}MB)`;
export const EXPERIMENT_STATUS = {
  NOT_REQUIRED: -1,
  NOT_STARTED: 0,
  DRAFT: 1,
  COMPLETED: 2,
  SYNCHRONIZED: 3,
  properties: {
    "-1": { color: "bg-dark", label: "Not required" },
    0: { color: "bg-secondary", label: "Not started" },
    1: { color: "bg-info", label: "Draft" },
    2: { color: "bg-success", label: "Completed" },
    3: { color: "bg-primary", label: "Synchronized" },
  },
};
export const MAX_ATTRIBUTES_SELECT_VALUES = 50;

/**
 * Create enum-like object with utils methods
 * Utils methods :
 * * iterateKeys : Iterate on non NaN keys
 * @param Object containing unique k-v associations
 */
function bimap(obj) {
  // check values unicity
  if (Object.values(obj).length !== new Set(Object.values(obj)).size) {
    console.warn("Impossible to convert object to enum-like object");
    return obj;
  }
  const enumObj = { ...obj };
  Object.entries(enumObj).forEach(([k, v]) => {
    enumObj[v] = k;
  });

  // add utils methods
  // Keep the reverse-mapped entries only: `isNaN` also let `iterateKeys`
  // itself through, since isNaN(function) is true.
  enumObj.iterateKeys = () =>
    Object.keys(enumObj).filter((k) => typeof enumObj[k] === "string");

  return Object.freeze(enumObj);
}

export const SESSION_STATUS = bimap({
  Initialized: 0,
  InProgress: 1,
  Completed: 2,
  Synchronized: 3,
  Partial: 4,
});

export const FILE_TYPE_LABEL = {
  plot_file: "Plot File",
  gcp_file: "GCP File",
  metadata_file: "Metadata File",
  sensor: "Sensor Model",
  flight_altitude: "Flight altitude (in m)",
  gap: "Gap (in cm)",
  archive_filename: "Archive file name",
};

// Derive downloadable categories from FILE_TYPE_LABEL (all keys ending with '_file')
export const DOWNLOADABLE_FILE_CATEGORIES = Object.keys(FILE_TYPE_LABEL).filter(
  (key) => key.endsWith("_file")
);
export const CLIENT_REQUEST_TYPES = {
  plot_ratings_config: "plot_ratings_config",
};

export const DEFAULT_ERROR_MESSAGE =
  "An error occurred, please try again in a few minutes or get in touch with your cloverfield contact.";

export const DOWNLOAD_WITHOUT_RESULT = "No result available for this date.";

export const BULK_EXPORT_STARTED =
  "Your export has started. You will receive an email with a download link once it is ready.";

export const LARGE_EXPORT_WARNING =
  "You are about to export a large amount of data. It may take a while: you will receive an email with a download link once it is ready.";

export const ROLES = [
  {
    label: "Display results",
    value: "view_features",
  },
  {
    label: "Display plot map",
    value: "view_experiments",
  },
  {
    label: "View monitoring",
    value: "view_monitoring",
  },
  {
    label: "View analytics",
    value: "view_analytics",
  },
  {
    label: "View PCA dashboard",
    value: "view_pca",
  },
  {
    label: "View ANOVA dashboard",
    value: "view_anova",
  },
  {
    label: "Create site",
    value: "create_site",
  },
  {
    label: "Rate plots",
    value: "rate_plots",
  },
  {
    label: "Edit plot rating configuration",
    value: "edit_plot_rating_config",
  },
];

export const BASIC_ROLES = [
  "view_features",
  "view_experiments",
  "view_monitoring",
  "view_analytics",
  "view_pca",
  "view_anova",
  "rate_plots",
];

export const HIPHEN_GREEN = "#8FBF48";
export const AUC_COLOR = "#e7c368";
export const PLOT_RATING_COLOR = "#e65e69";
export const CUSTOM_DATA_COLOR = "#48a9bf";
export const SCOUTING_COLOR = "#C62828";
export const COMPANY_HIPHEN = "Hiphen";

// Visualisation
export const AD_MAX_VISUALIZED_ELEMENTS = 3;
export const AD_DATE_FORMAT = "DD-MM-YY";
export const AD_COL_CLASSES = "col d-flex flex-column";

export const DEFAULT_BLACKLISTED_TRAITS = Object.freeze([
  "Latitude",
  "Longitude",
  "latitude",
  "longitude",
  "plot_id",
  "client_id",
  "X",
  "Y",
  "x",
  "y",
]);

export const RESERVED_PROPERTY_KEYS = Object.freeze([
  "modality",
  "experiment",
  "genotype",
  "layer",
  "control_variety",
]);

export const GENOTYPE_PROPERTY = "genotype";
export const MODALITY_PROPERTY = "modality";
export const EXPERIMENT_PROPERTY = "experiment";
export const LAYER_PROPERTY = "layer";
export const CLIENT_ID_PROPERTY = "client_id";

export const PLOT_LABEL_ID = "displayId";

export const LABEL_ALL = "All";
export const LABEL_OVERVIEW = "Overview";

export const PLOT_VIEW_MODES = Object.freeze({
  PROPERTIES: "properties",
  IMAGES: "images",
});

export const OTHER_NUMERICAL_PROPERTIES_GROUP = "My custom data";

export const BBCH_STAGES = Object.freeze({
  0: "Germination, sprouting, bud development",
  1: "Leaf development",
  2: "Formation of side shoots, tillering",
  3: "Stem elongation or rosette growth, shoot development",
  4: "Development of harvestable vegetative plant parts, bolting",
  5: "Inflorescence emergence, heading",
  6: "Flowering",
  7: "Development of fruit",
  8: "Ripening or maturity of fruit and seed",
  9: "Senescence, beginning of dormancy",
  10: "Post-harvest",
});

export const DEFAULT_TRAIT_STYLE = {
  opacity: true,
  color: ["#ff0033", "#19e633"],
  intervalDefault: false,
  intervalMin: 0,
  intervalMax: 100,
  step: 5,
};

export const MATRIX_FILL_METHODS = Object.freeze({
  DROP: "drop",
  MEAN: "mean",
});

export const IMPORTED_DATA_TYPES = Object.freeze({
  NUMERIC: "numeric",
  ATTRIBUTE: "attribute",
});

export const FEATURE_PROPERTY_CATEGORIES = Object.freeze({
  HIPHEN: {
    label: "Traits",
    icon: (
      <i className="hiphen-logo" style={{ width: "14px" }} alt="hiphen-logo" />
    ),
    color: HIPHEN_GREEN,
    light: "#D5E7BA",
    priority: 1,
    attribute: false,
    className: "hiphen-trait",
  },
  IMPORTED: {
    label: "My custom numerical data",
    icon: (
      <i className="fa-solid fa-hashtag" style={{ color: CUSTOM_DATA_COLOR }} />
    ),
    color: CUSTOM_DATA_COLOR,
    light: "#BADFE7",
    priority: 2,
    attribute: false,
    className: "custom-data",
  },
  INTEGRATIVE: {
    label: "Integrative traits",
    icon: <i className="fa-solid fa-chart-area" style={{ color: AUC_COLOR }} />,
    color: AUC_COLOR,
    light: "#F7EBCD",
    priority: 3,
    attribute: false,
    className: "integrative-trait",
  },
  RATING: {
    label: "Plot ratings",
    icon: (
      <i className="fa-solid fa-pencil" style={{ color: PLOT_RATING_COLOR }} />
    ),
    color: PLOT_RATING_COLOR,
    light: "#F6C2C7",
    priority: 4,
    attribute: true,
    className: "attribute",
  },
  RESERVED: {
    label: "Attributes",
    icon: <i className="fa-solid fa-tag" />,
    color: "black",
    light: "darkgray",
    priority: 5,
    attribute: true,
    className: "reserved-property attribute",
  },
  DEFAULT: {
    label: "Attributes",
    icon: <i className="fa-solid fa-tag discrete-icon" />,
    color: "darkgray",
    light: "#dcdcdc",
    priority: 6,
    attribute: true,
    className: "attribute",
  },
});

export const ANALYTICS_EVENTS = {
  AUC_MODE: {
    eventName: "analytics/AucMode",
    eventLabel: "switch AUC mode",
    eventCategory: "switch button",
  },
  ANALYTICS_DOWNLOAD_BUTTON: {
    eventName: "analytics/DownloadButton",
    eventLabel: "download",
    eventCategory: "button",
  },
  ANALYTICS_AF_BUTTON: {
    eventName: "analytics/AFButton",
    eventLabel: "open AF drawer",
    eventCategory: "button",
  },
  MAP_AF_BUTTON: {
    eventName: "maps/AFButton",
    eventLabel: "open AF drawer",
    eventCategory: "button",
  },
  RATE_PLOTS: {
    eventName: "analytics/RatePlots",
    eventLabel: "open rate plots modal",
    eventCategory: "button",
  },
  AF_APPLY_FILTER: {
    eventName: "analytics/AFApplyFilter",
    eventLabel: "apply filter",
    eventCategory: "button",
  },
  AF_SAVE_SCOPE: {
    eventName: "analytics/AFSaveScope",
    eventLabel: "save scope",
    eventCategory: "button",
  },
  UPLOAD_SAVE: {
    eventName: "upload/upload&save",
    eventLabel: "save and upload mission",
    eventCategory: "button",
  },
  RANDOM_SELECT: {
    eventName: "analytics/RandomSelect",
    eventLabel: "random select",
    eventCategory: "button",
  },
  UPLOAD_YOUR_OWN_DATA: {
    eventName: "analytics/UploadYourOwnData",
    eventLabel: "upload your own data",
    eventCategory: "button",
  },
  OPEN_UPLOAD_YOUR_OWN_DATA: {
    eventName: "analytics/OpenUploadYourOwnData",
    eventLabel: "open upload your own data",
    eventCategory: "button",
  },
  ADD_FILE_UPLOAD_YOUR_OWN_DATA: {
    eventName: "analytics/AddFileUploadYourOwnData",
    eventLabel: "add file upload your own data",
    eventCategory: "drop",
  },
  UPLOAD_YOUR_OWN_DATA_DOWNLOAD_CSV_TEMPLATE: {
    eventName: "analytics/UploadYourOwnDataDownloadCSVTemplate",
    eventLabel: "download csv template",
    eventCategory: "button",
  },
  SELECT_FROM_GENOTYPE_ANALYZER: {
    eventName: "analytics/SelectFromGenotypeAnalyzer",
    eventLabel: "select from genotype analyzer",
    eventCategory: "select",
  },
  SELECT_FROM_SCATTER_TOOL: {
    eventName: "analytics/SelectFromScatterTool",
    eventLabel: "select from scatter tool",
    eventCategory: "select",
  },
  SELECT_FROM_PCA_SCATTER: {
    eventName: "pca/SelectFromScatter",
    eventLabel: "select from pca scatter",
    eventCategory: "select",
  },
  CREATE_SITE: {
    eventName: "clientdashboard/CreateSite",
    eventLabel: "create site",
    eventCategory: "button",
  },
  ANALYTICS_IS_BEING_RENDERED: {
    eventName: "analytics",
    eventLabel: "analytics is rendered on trial",
    eventCategory: "page_view",
  },
  MAP_IS_BEING_RENDERED: {
    eventName: "map",
    eventLabel: "map is rendered on trial",
    eventCategory: "page_view",
  },
  HELP_TOOLTIP_OPENED: {
    eventName: "helpTooltipOpened",
    eventLabel: "help tooltip is opened",
    eventCategory: "button",
  },
  COMPUTE_ANOVA: {
    eventName: "computeAnova",
    eventLabel: "Requested anova computation",
    eventCategory: "button",
  },
  LOAD_RECENT_ANOVA: {
    eventName: "loadRecentAnova",
    eventLabel: "Click on anova history item to load previous anova",
    eventCategory: "button",
  },
  DISPLAY_TUKEY_HSD: {
    eventName: "displayTukeyHSD",
    eventLabel: "Click on display tukey HSD",
    eventCategory: "button",
  },
};

export const PLOTMAP_STATUS = {
  NOT_DELIVERED: 0,
  WAITING_FOR_VALIDATION: 1,
  VALIDATED: 2,
  CHANGES_REQUESTED: 3,
  properties: {
    0: { label: "Not delivered", color: "#c0c0c0", icon: "fa-solid fa-ban" },
    1: {
      label: "Waiting for validation",
      color: "#ffcb3d",
      icon: "fa-regular fa-clock",
    },
    2: { label: "Validated", color: "#8fbf48", icon: "fa-solid fa-check" },
    3: {
      label: "Changes requested",
      color: "#dc3545",
      icon: "fa-solid fa-triangle-exclamation",
    },
  },
};

// Why a site/trial "requires client action", or null when nothing is pending.
// The tile renders this straight, because an orange rail that never says what it
// wants is the whole of R1-13.
export const clientActionReason = (item) => {
  if (item.experiment_status === EXPERIMENT_STATUS.NOT_STARTED)
    return "Field map not started";
  if (item.experiment_status === EXPERIMENT_STATUS.DRAFT)
    return "Field map still a draft";
  if (item.plotmap_status === PLOTMAP_STATUS.WAITING_FOR_VALIDATION)
    return "Plot map awaiting your validation";
  return null;
};

// Derived from the reason rather than restating the criterion: two copies of the
// same test drift apart the moment one of them gains a case. Shared so the map's
// FilterByStatus and the campaign page apply the exact same rule.
export const requiresClientAction = (item) => clientActionReason(item) !== null;

export const TRIAL_STATUS = {
  ACTION_REQUIRED: {
    label: "Action required",
    color: "#ffcb3d",
    icon: <i className="fa-solid fa-wrench" />,
  },
  IN_PREPARATION: {
    label: "In preparation",
    color: "#c0c0c0",
    icon: <i className="fa-solid fa-clock" />,
  },
  VALIDATED: {
    label: "Validated",
    color: "#8FBF48",
    icon: <i className="fa-solid fa-check" />,
  },
};

export const FLOW_STATUS_MESSAGES = {
  PLOTMAP_WAITING_VALIDATION: {
    color: "#d19200",
    icon: "fa-solid fa-triangle-exclamation",
    text: "This site requires a validation from the client",
  },
  PLOTMAP_VALIDATED: {
    color: "#7db923ff",
    icon: "fa-solid fa-check",
    text: "This site is ready",
  },
  PLOTMAP_CHANGES_REQUESTED: {
    color: "#d19200",
    icon: "fa-solid fa-triangle-exclamation",
    text: "Changes have been requested by the client",
  },
  EXPERIMENT_NOT_REQUIRED: {
    color: "",
    icon: "fa-solid fa-pencil",
    text: "The drawing of the field map will be handled by Hiphen",
  },
  EXPERIMENT_SYNCHRONIZED: {
    color: "",
    icon: "fa-solid fa-circle-info",
    text: "Field map is complete, waiting for the plot map to be delivered",
  },
};

export const MISSION_MONITORING_STATIC_STATUS = Object.freeze([
  "PROCESSING",
  "ON HOLD",
  "DELIVERED",
  "ABORTED",
]);

export const MISSION_MONITORING_STATIC_GROUPS = Object.freeze({
  CONTRACT: "campaign",
  COUNTRY: "country",
  CROP: "crop",
  SENSOR: "sensor_type",
});

export const MISSION_MONITORING_STATIC_FLAGS = Object.freeze([
  "Dataset incomplete",
  "Dataset incorrect",
  "Images blurred",
  "Images overexposed",
  "Low resolution",
  "GCP(s) missing",
  "GCP coordinates missing",
  "GCP coordinates incorrect",
  "Plot file missing",
  "Plot file incorrect",
  "Radiometric calibration",
  "Weeds infestation",
  "Other",
]);

export const MISSION_MONITORING_STATIC_STATUS_ICONS = Object.freeze({
  ON_HOLD: "fa-circle-pause",
  DELIVERED: "fa-circle-check",
  PROCESSING: "fa-arrows-rotate",
  ABORTED: "fa-ban",
});

export const MISSION_MONITORING_STATIC_STATUS_COLORS = Object.freeze({
  ON_HOLD: "#ffd166",
  DELIVERED: "#06d6a0",
  PROCESSING: "#0A67C9",
  ABORTED: "#e3e3e3",
});
export const ACQUISITION_REPORT_STATUS_COLORS = Object.freeze({
  Validated: "#54D62C",
  Warning: "#FFC107",
  Unknown: "#637381",
  Rejected: "#FF4842",
});

export const MISSION_MONITORING_COLORWAY = Object.freeze([
  "#58508d",
  "#e32986",
  "#bc5090",
  "#ff6361",
  "#ffa600",
]);

export const MISSION_MONITORING_FLAG_COLORS = Object.freeze({
  RAISED: "#e00719",
  RESOLVED: "#0b9e12",
});

export const MiSSION_MONITORING_STATUS = "status";
export const MiSSION_MONITORING_SUBSTATUS = "substatus";

// Free-text substatus, matched on its humanized + lowercased form.
export const MISSION_MONITORING_CLIENT_ACTION_SUBSTATUS =
  "client action required";

export const MISSION_MONITORING_THUMB_STEP = "thumb_step";
export const MISSION_MONITORING_THUMB_FLAG = "thumb_flag";

export const MiSSION_MONITORING_FLAG_COUNT = "count_active_flags";
export const MiSSION_MONITORING_COMMENT_COUNT = "count_unread_comments";

export const NULL_GROUP_LABEL = "N/A";

export const COMMENT_ID_QUERY_PARAM = "comment";

export const POWERDASH_DATE_FORMAT = "YYYY-MM-DD";

export const MAX_GENOTYPE_SELECTION = 600;
export const CONTROL_VARIETY_YES = "yes";

export const EXPORT_FORMATS = Object.freeze({
  CSV: { ext: ".csv", mime: "text/csv" },
  JSON: { ext: ".json", mime: "text/json" },
});

export const CHART_MODEBAR_BUTTONS_TO_REMOVE = [
  "pan",
  "select2d",
  "zoom2d",
  "lasso2d",
  "zoomIn2d",
  "zoomOut2d",
  "autoScale2d",
  "resetScale2d",
];

export const COMMON_PLOT_CONFIG = {
  responsive: true,
  displaylogo: false,
  modeBarButtonsToRemove: CHART_MODEBAR_BUTTONS_TO_REMOVE,
};

export const ADVANCED_FILTERING_DATASET_COLORS = Object.freeze({
  includedData: HIPHEN_GREEN,
  dataToInclude: "#D6E8B3",
  dataToExclude: "#FFA500",
  excludedData: "lightgray",
});
